import React, { useState } from "react";
import { toast } from "react-toastify";
import { onImageHandler } from "../../components/UploadImageOnBucket";

export const emptyReview = () => ({
  name: "",
  location: "",
  date: "",
  image: "",
  videoLink: "",
  title: "",
  description: "",
});

// map a review from the API into form state (date input needs YYYY-MM-DD)
export const reviewFromApi = (review = {}) => ({
  ...emptyReview(),
  ...review,
  date: review?.date ? String(review.date).slice(0, 10) : "",
});

export const reviewsFromApi = (reviews = []) => reviews.map(reviewFromApi);

const isReviewEmpty = (review) =>
  Object.keys(emptyReview()).every((field) => !String(review?.[field] || "").trim());

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

/* =========================
   FIELDS OF A SINGLE REVIEW
========================= */
export const ReviewFields = ({ review, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ ...review, [name]: value });
  };

  const handleImage = async (e) => {
    setUploading(true);
    try {
      const data = await onImageHandler(e);
      if (typeof data === "string") {
        toast.error(data);
      } else {
        onChange({ ...review, image: data[1].location });
      }
    } catch (error) {
      toast.error("Something went worng with AWS bucket");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="row">
      <div className="col-md-6 form-group">
        <label>Name</label>
        <input type="text" name="name" className="form-control" value={review.name} onChange={handleChange} />
      </div>
      <div className="col-md-6 form-group">
        <label>Location</label>
        <input type="text" name="location" className="form-control" value={review.location} onChange={handleChange} />
      </div>
      <div className="col-md-6 form-group">
        <label>Date</label>
        <input type="date" name="date" className="form-control" value={review.date} onChange={handleChange} />
      </div>
      <div className="col-md-6 form-group">
        <label>Title</label>
        <input type="text" name="title" className="form-control" value={review.title} onChange={handleChange} />
      </div>
      <div className="col-md-6 form-group">
        <label>Image</label>
        <input
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          className="form-control"
          onChange={handleImage}
          disabled={uploading}
        />
        {uploading && <small>Uploading...</small>}
        {review.image && (
          <div className="mt-2 d-flex align-items-center">
            <img src={review.image} alt="review" style={{ width: 80, height: 80, objectFit: "cover" }} />
            <button
              type="button"
              className="btn btn-sm btn-link text-danger"
              onClick={() => onChange({ ...review, image: "" })}
            >
              Remove
            </button>
          </div>
        )}
      </div>
      <div className="col-md-6 form-group">
        <label>Video Link</label>
        <input
          type="url"
          name="videoLink"
          className="form-control"
          placeholder="https://"
          value={review.videoLink}
          onChange={handleChange}
        />
      </div>
      <div className="col-12 form-group">
        <label>Description</label>
        <textarea name="description" className="form-control" value={review.description} onChange={handleChange} />
      </div>
    </div>
  );
};

/* =========================
   ADD PAGE - local list, saved together with the event page
========================= */
const ReviewSection = ({ reviews, setReviews }) => {
  const updateReview = (index, value) =>
    setReviews((prev) => prev.map((review, i) => (i === index ? value : review)));

  const addReview = () => setReviews((prev) => [...prev, emptyReview()]);

  const removeReview = (index) => {
    if (!window.confirm("Remove this review?")) return;
    setReviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="form-group mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="mb-0">Reviews</label>
        <button type="button" className="Button" onClick={addReview}>
          + Add Review
        </button>
      </div>

      {!reviews.length && <p className="text-muted">No reviews added.</p>}

      {reviews.map((review, index) => (
        <div key={index} className="border rounded p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Review {index + 1}</strong>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeReview(index)}>
              Delete
            </button>
          </div>
          <ReviewFields review={review} onChange={(value) => updateReview(index, value)} />
        </div>
      ))}
    </div>
  );
};

/* =========================
   EDIT PAGE - saved review cards, each action hits the review APIs directly
========================= */
export const ReviewCardList = ({ reviews = [], loading, onAdd, onUpdate, onDelete }) => {
  // editingId: review _id being edited, "new" for the add form, null when closed
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyReview());

  const openAdd = () => {
    setDraft(emptyReview());
    setEditingId("new");
  };

  const openEdit = (review) => {
    setDraft(reviewFromApi(review));
    setEditingId(review._id);
  };

  const closeForm = () => {
    setEditingId(null);
    setDraft(emptyReview());
  };

  const handleSave = async () => {
    if (isReviewEmpty(draft)) {
      toast.error("Please fill at least one review field");
      return;
    }
    const { _id, ...payload } = draft;
    const res = editingId === "new" ? await onAdd(payload) : await onUpdate(editingId, payload);
    if (!res?.error) closeForm();
  };

  const handleDelete = (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    if (editingId === reviewId) closeForm();
    onDelete(reviewId);
  };

  const renderForm = () => (
    <div className="border rounded p-3 mb-3">
      <strong className="d-block mb-2">{editingId === "new" ? "New Review" : "Edit Review"}</strong>
      <ReviewFields review={draft} onChange={setDraft} />
      <div className="d-flex gap-2">
        <button type="button" className="Button" disabled={loading} onClick={handleSave}>
          {loading ? "Saving..." : "Save Review"}
        </button>
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={closeForm}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="form-group mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="mb-0">Reviews ({reviews.length})</label>
        {editingId !== "new" && (
          <button type="button" className="Button" onClick={openAdd}>
            + Add Review
          </button>
        )}
      </div>

      {editingId === "new" && renderForm()}

      {!reviews.length && editingId !== "new" && <p className="text-muted">No reviews added.</p>}

      <div className="row">
        {reviews.map((review) =>
          editingId === review._id ? (
            <div key={review._id} className="col-12">
              {renderForm()}
            </div>
          ) : (
            <div key={review._id} className="col-md-6 mb-3">
              <div className="card h-100">
                {review.image && (
                  <img
                    src={review.image}
                    alt={review.title || "review"}
                    className="card-img-top"
                    style={{ height: 160, objectFit: "cover" }}
                  />
                )}
                <div className="card-body">
                  {review.title && <h6 className="card-title mb-1">{review.title}</h6>}
                  <p className="text-muted small mb-2">
                    {[review.name, review.location, formatDate(review.date)].filter(Boolean).join(" · ")}
                  </p>
                  {review.description && <p className="card-text small mb-2">{review.description}</p>}
                  {review.videoLink && (
                    <a href={review.videoLink} target="_blank" rel="noopener noreferrer" className="small">
                      Watch video
                    </a>
                  )}
                </div>
                <div className="card-footer bg-transparent d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={loading}
                    onClick={() => openEdit(review)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={loading}
                    onClick={() => handleDelete(review._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ReviewSection;
